import { Box, Skeleton } from '@mui/material';
import { useBlock } from '@/blocks/application/hooks/useBlock';
import { usePreviewVisibility } from '@/common/presentation/components/library';
import { BlockPreviewBoundary } from '@/blocks/presentation/components/BlockPreviewBoundary';
import { LivePreview } from '@/blocks/presentation/components/LivePreview';

export interface BlockThumbnailProps {
  blockId: string;
}

/**
 * A Block Library card's hero preview (Figma `bl.library.default`, node `6:3`) — the live published
 * version (never the open draft), painted with dev's existing `LivePreview` (the same "wrap the version as a one-block
 * composition" trick the block editor's own preview pane uses) inside the shared, cropped
 * `LibraryPreviewFrame` (Part 2 kit; `LibraryCard` mounts that frame around whatever `preview`
 * node it is given).
 *
 * ONE DETAIL FETCH PER VISIBLE CARD (LLAMS-617 Part 3 plan): this component owns its own
 * visibility gate (`usePreviewVisibility`, the library kit's `useInViewOnce` alias) and passes it
 * straight to `useBlock`'s `enabled` option, rather than relying solely on the outer
 * `LibraryPreviewFrame` deferring when this component MOUNTS. A gallery of N cards therefore
 * fires at most N block-detail requests, spread across scroll, never N requests on first paint —
 * the "eager live render of every card" defect the prototype's own `BlockLibraryCard` had (it
 * called `useBlock` unconditionally from the CARD, one fetch per card at mount).
 */
export function BlockThumbnail({ blockId }: BlockThumbnailProps) {
  const { ref, inView } = usePreviewVisibility<HTMLDivElement>();
  const { data, isLoading, error } = useBlock(blockId, undefined, { enabled: inView });

  // D-9 / BL-02: the card shows the LIVE (published) version. `currentVersion` is the EDITABLE
  // version - the open draft, or null for an archived block - so it is only the fallback.
  const version = data?.publishedVersion ?? data?.currentVersion ?? null;

  if (!inView) {
    return <Box ref={ref} aria-hidden sx={{ width: '100%', height: '100%' }} data-state="deferred" />;
  }

  if (isLoading) {
    return (
      <Box ref={ref} role="status" aria-label="Loading preview" sx={{ width: '100%', height: '100%' }} data-state="loading">
        <Skeleton variant="rectangular" width="100%" height="100%" />
      </Box>
    );
  }

  // BL-10: an unsupported kind has no renderer, so painting its placeholder content would be wrong.
  if (data?.block?.isSupportedKind === false) {
    return (
      <Box ref={ref} role="img" aria-label="No preview for this kind" sx={{ width: '100%', height: '100%' }} data-state="unsupported" />
    );
  }

  if (error || !version) {
    return (
      <Box
        ref={ref}
        role="img"
        aria-label={error ? 'Preview unavailable' : 'No preview yet'}
        sx={{ width: '100%', height: '100%' }}
        data-state={error ? 'error' : 'empty'}
      />
    );
  }

  return (
    <Box ref={ref} aria-hidden sx={{ width: '100%', height: '100%' }} data-state="ready">
      <BlockPreviewBoundary>
        <LivePreview draft={{ content: version.content, style: version.style }} />
      </BlockPreviewBoundary>
    </Box>
  );
}
